import { ReactNode } from 'react';
import { Icon, XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { ActionVariant } from './ActionBar.types';
import { useActionBarResponsiveClasses } from './useActionBarResponsiveClasses';

interface ActionBarItemProps {
    icon?: Icon;
    label: string;
    onClick?: () => void;
    onClear?: () => void;
    variant?: ActionVariant;
    disabled?: boolean;
    maxWidth?: string;
    active?: boolean;
    className?: string;
    trailingBadge?: ReactNode;
}

export function ActionBarItem({
    icon: IconComponent,
    label,
    onClick,
    onClear,
    variant = ActionVariant.DEFAULT,
    disabled,
    maxWidth,
    active = false,
    className,
    trailingBadge,
}: ActionBarItemProps) {
    const responsive = useActionBarResponsiveClasses();
    const isPrimary = variant === ActionVariant.PRIMARY;
    const isDanger = variant === ActionVariant.DANGER;
    const iconColor = isDanger
        ? 'text-danger-200'
        : active
          ? 'text-white-100'
          : 'text-grey-300 group-hover:text-grey-200';
    const textColor = isDanger
        ? 'text-danger-200'
        : active
          ? 'text-grey-100'
          : 'text-grey-300 group-hover:text-grey-200';

    const handleClearClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClear?.();
    };

    const showClearButton = active && onClear;

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            aria-label={label}
            className={cn(
                'group relative flex items-center h-8 rounded-md',
                !isPrimary &&
                    (IconComponent ? responsive.trailingPadding : 'pr-5'),
                isPrimary && 'bg-grey-550  hover:bg-grey-500 px-2 rounded-full',
                disabled && 'opacity-50 cursor-not-allowed',
                active && 'z-20',
                className,
            )}
        >
            <span
                className={cn(
                    'flex items-center gap-1.5 transition-transform duration-150 ease-out',
                    showClearButton && responsive.hoverShift,
                )}
            >
                {IconComponent && (
                    <span className="relative inline-flex size-4 flex-shrink-0 items-center justify-center">
                        <IconComponent className={cn('size-4', iconColor)} />
                        {active && (
                            <span
                                className="pointer-events-none absolute bottom-0 right-0 w-[3px] h-[3px] rounded-full bg-white-100"
                                aria-hidden="true"
                            />
                        )}
                    </span>
                )}
                <span
                    className={cn(
                        'text-sm font-medium whitespace-nowrap',
                        IconComponent && responsive.label,
                        textColor,
                        maxWidth && 'truncate',
                    )}
                    style={maxWidth ? { maxWidth } : undefined}
                >
                    {label}
                </span>
                {trailingBadge}
            </span>
            {showClearButton && (
                <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClearClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClearClick(e as unknown as React.MouseEvent);
                        }
                    }}
                    className={cn(
                        'absolute right-2 size-4 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity text-white-100 hover:text-grey-200 cursor-pointer outline-none focus:opacity-100',
                        responsive.closeIcon,
                    )}
                    aria-label="Clear filter"
                >
                    <XIcon size={16} />
                </span>
            )}
        </button>
    );
}
